import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, RefreshCw, Users, Clock, AlertCircle } from 'lucide-react';

const INTERVIEW_API_BASE = process.env.REACT_APP_API_BASE;

export default function LiveInterviews() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchActiveSessions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${INTERVIEW_API_BASE}/observer/active-sessions`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch active sessions');
      const data = await response.json();
      setSessions(data.active_sessions || []);
    } catch (err) {
      console.error('Error fetching active sessions:', err);
      setError('Failed to load active sessions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveSessions();
    const interval = setInterval(fetchActiveSessions, 10000);
    return () => clearInterval(interval);
  }, [fetchActiveSessions]);

  const handleJoinSession = (sessionId) => {
    navigate(`/observe/${sessionId}`);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-ink-100">Live Interviews</h2>
          <p className="text-sm text-ink-300 mt-1">
            Watch ongoing interviews in real-time
          </p>
        </div>
        <button
          onClick={fetchActiveSessions}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-300 bg-ink-900 border border-ink-700 rounded-lg hover:bg-ink-800 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {loading && sessions.length === 0 ? (
        <div className="text-center py-12 text-ink-400">Loading active sessions...</div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12">
          <Eye className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-ink-400">No active interviews at the moment</p>
          <p className="text-sm text-ink-500 mt-1">
            Sessions will appear here when candidates start their interviews
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {sessions.map((session) => (
            <div
              key={session.session_id}
              className="bg-ink-900 border border-ink-700 rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-base font-semibold text-ink-100">
                      {session.candidate_name}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                      Live
                    </span>
                  </div>
                  <p className="text-sm text-ink-300">
                    {session.job_title}
                    {session.department && ` - ${session.department}`}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-ink-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {session.duration_minutes || 0} min
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {session.observer_count || 0} watching
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleJoinSession(session.session_id)}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-400 transition-colors text-sm font-medium"
                >
                  <Eye className="w-4 h-4" />
                  Observe
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
